import { useMemo } from 'react';
import { useAsync, useExit } from '@wix/cli-ui-kit';
import type { Headers } from '@wix/cli-http-client';
import { isType } from 'variant';
import type { UserInfo } from '../schemas.js';
import { createSiteAuthStrategy } from '../auth-strategies/index.js';
import type { AuthStrategyState } from '../auth-strategies/index.js';

interface UseSiteAuthOptions {
  siteId: string;
  onError?: (error: unknown) => void;
  onLogin?: (userInfo: UserInfo) => void;
}

type UseSiteAuthResult =
  | {
      isLoading: boolean;
      isError: boolean;
      isAuthenticated: false;
      authState: null;
      error?: unknown;
    }
  | {
      isLoading: false;
      isError: false;
      isAuthenticated: true;
      authState: AuthStrategyState;
      getAccessToken: () => Promise<string>;
      getRequestHeaders?: () => Headers;
      userInfo: UserInfo;
    };

export const useSiteAuth = ({
  siteId,
  onError,
  onLogin,
}: UseSiteAuthOptions): UseSiteAuthResult => {
  const exit = useExit();
  const authStrategy = useMemo(() => createSiteAuthStrategy(siteId), [siteId]);

  const { status } = useAsync(authStrategy.isLoggedIn, [], {
    onSuccess: (result) => {
      if (result) {
        onLogin?.(result.userInfo);
      }
    },
    onError: onError ?? exit,
  });

  if (isType(status, 'Loading')) {
    return {
      isLoading: true,
      isError: false,
      isAuthenticated: false,
      authState: null,
    };
  }

  if (isType(status, 'Error')) {
    return {
      isLoading: false,
      isError: true,
      isAuthenticated: false,
      authState: null,
      error: status.error,
    };
  }

  const authState = status.result;
  if (authState == null) {
    return {
      isLoading: false,
      isError: false,
      isAuthenticated: false,
      authState: null,
    };
  }

  return {
    isLoading: false,
    isError: false,
    isAuthenticated: true,
    authState,
    getAccessToken: authState.getAccessToken,
    getRequestHeaders: authState.getRequestHeaders,
    userInfo: authState.userInfo,
  };
};
